import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../store';
import CommerceTokenUnavailableModal from '../components/CommerceTokenUnavailableModal';
import { InstabilityModal } from '../components/InstabilityModal';
import useIsCommerceUser from '../hooks/useIsCommerceUser';
import useModalPriority, { ModalPriorities } from '../hooks/useModalPriority';
import { GetFlag } from '../utils/featureFlag';

interface LoadingModalContextData {
  isLoadingModalOpen: boolean;
  setLoadingModalOpen: (isOpen: boolean) => void;
  showInstabilityModal: boolean;
  setShowInstabilityModal: (isOpen: boolean) => void;
  canShowInstabilityModal: boolean;
  blockInstabilityModal: () => void;
  shouldShowOnlyLoading: boolean;
  shouldShowOnlyInstability: boolean;
  isCommerceTokenUnavailableModalOpen: boolean;
  setCommerceTokenUnavailableModalOpen: (isOpen: boolean) => void;
}

const LoadingModalContext = createContext<LoadingModalContextData | undefined>(
  undefined
);

export const LoadingModalProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [isLoadingModalOpen, setIsLoadingModalOpen] = useState(false);
  const [showInstabilityModal, setShowInstabilityModal] = useState(true);
  const [isBlocked, setIsBlocked] = useState(false);
  const [, setCanShowInstability] = useState(false);
  const [
    isCommerceTokenUnavailableModalOpen,
    setCommerceTokenUnavailableModalOpen
  ] = useState(false);

  const shouldShowOnlyLoading = isLoadingModalOpen;
  const userLoaded = useSelector(
    (appState: ApplicationState) => appState.user.userLoaded
  );

  const shouldShowOnlyInstability =
    !isLoadingModalOpen && showInstabilityModal && !isBlocked && userLoaded;

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (!isLoadingModalOpen && isBlocked) {
      timer = setTimeout(() => setIsBlocked(false), 500);
    }

    if (!isLoadingModalOpen && showInstabilityModal && !isBlocked) {
      timer = setTimeout(() => setCanShowInstability(true), 500);
    } else {
      setCanShowInstability(false);
    }

    return () => clearTimeout(timer);
  }, [isLoadingModalOpen, isBlocked]);

  const setLoadingModalOpen = (isOpen: boolean) => {
    setIsLoadingModalOpen(isOpen);
    if (isOpen) {
      setIsBlocked(true);
    }
  };

  const showCommerceTokenModal = useSelector(
    (appState: ApplicationState) => appState.auth.showCommerceTokenModal
  );

  const isCommerceUser = useIsCommerceUser();

  const instabilityModalFlag = GetFlag('instabilidade_modal');
  const [showInstabilityModalPriority, instabilityModalActions] =
    useModalPriority(
      'modalInstability',
      ModalPriorities.Instability,
      instabilityModalFlag && shouldShowOnlyInstability,
      undefined,
      false
    );

  return (
    <>
      <LoadingModalContext.Provider
        value={{
          isLoadingModalOpen,
          setLoadingModalOpen,
          showInstabilityModal,
          setShowInstabilityModal,
          canShowInstabilityModal: shouldShowOnlyInstability,
          blockInstabilityModal: () => setIsBlocked(true),
          shouldShowOnlyLoading,
          shouldShowOnlyInstability,
          isCommerceTokenUnavailableModalOpen,
          setCommerceTokenUnavailableModalOpen
        }}
      >
        {children}
      </LoadingModalContext.Provider>
      {showInstabilityModalPriority && (
        <InstabilityModal
          onCloseModal={() => {
            setShowInstabilityModal(false);
            instabilityModalActions.close();
          }}
        />
      )}
      {showCommerceTokenModal && isCommerceUser && (
        <CommerceTokenUnavailableModal />
      )}
    </>
  );
};

export const useLoadingModal = (): LoadingModalContextData => {
  const context = useContext(LoadingModalContext);
  if (!context) {
    throw new Error(
      'useLoadingModal must be used within a LoadingModalProvider'
    );
  }
  return context;
};
